import React, { useState, useEffect } from 'react';
import GlobalSimulationControls from './GlobalSimulationControls';
import ViewOptionsRow from './ViewOptionsRow';
import ExpirationFilterChips from './ExpirationFilterChips';
import TickerStoriesGrid from './TickerStoriesGrid';
import TickerStoryView from './TickerStoryView';
import {
  groupResultsByTicker,
  extractUniqueExpirations
} from '../../utils/screenerDataHelpers';
import '../../styles/MobileScreener.css';

/**
 * Main mobile results container
 * Orchestrates all mobile-specific components for ticker stories view
 */
const MobileResultsView = ({ results }) => {
  // State for mobile view
  const [resultsGrouped, setResultsGrouped] = useState({});
  const [allExpirations, setAllExpirations] = useState([]);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [activeExpiration, setActiveExpiration] = useState('all');
  const [storyViewOpen, setStoryViewOpen] = useState(false);
  const [currentTicker, setCurrentTicker] = useState(null);

  // View options state
  const [isGrouped, setIsGrouped] = useState(true); // true = grouped by ticker, false = individual contracts
  const [sortBy, setSortBy] = useState('gain'); // 'gain' or 'expiration'

  // Original settings from queue run (for Reset functionality)
  const [originalSettings, setOriginalSettings] = useState(null);

  // Expiration-specific simulation settings
  const [expirationSettings, setExpirationSettings] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('screener_expiration_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved expiration settings:', e);
      }
    }
    // Default settings
    return {
      'all': { days: 45, gain: 10 }
    };
  });

  // Save expiration settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('screener_expiration_settings', JSON.stringify(expirationSettings));
  }, [expirationSettings]);

  // Group results and extract expirations when results change
  useEffect(() => {
    if (Array.isArray(results) && results.length > 0) {
      const grouped = groupResultsByTicker(results);
      setResultsGrouped(grouped);

      const expirations = extractUniqueExpirations(results);
      setAllExpirations(expirations);

      // Get queue values from first result
      const firstResult = results[0];
      const queueDays = firstResult['Days to Gain'] || 45;
      const queueGain = firstResult['Underlying Gain %'] || 10;

      // Capture original settings from first result on initial load
      if (!originalSettings) {
        setOriginalSettings({
          days: queueDays,
          gain: queueGain
        });
      }

      // Initialize expiration settings for new dates using original queue values

      const newSettings = { ...expirationSettings };
      expirations.forEach(exp => {
        if (!newSettings[exp]) {
          // Use original queue values instead of hardcoded defaults
          newSettings[exp] = { days: queueDays, gain: queueGain };
        }
      });
      // Also update 'all' setting to match queue values if it's still at defaults
      if (newSettings['all']?.days === 45 && newSettings['all']?.gain === 10) {
        newSettings['all'] = { days: queueDays, gain: queueGain };
      }
      if (JSON.stringify(newSettings) !== JSON.stringify(expirationSettings)) {
        setExpirationSettings(newSettings);
      }
    }
  }, [results]);

  // Handle ticker bubble click
  const handleTickerClick = (ticker) => {
    setCurrentTicker(ticker);
    setStoryViewOpen(true);
  };

  // Handle story view close
  const handleCloseStoryView = () => {
    setStoryViewOpen(false);
    setCurrentTicker(null);
  };

  // Handle contract gain updates from sliders
  const handleContractGainUpdate = (ticker, strike, expiration, newGain, newPremium) => {
    setResultsGrouped(prev => {
      const updated = { ...prev };
      if (updated[ticker]) {
        updated[ticker] = updated[ticker].map(contract => {
          // Match contract by strike and expiration
          if (
            (contract.Strike || contract.strike) === strike &&
            (contract.Expiration || contract.expiration) === expiration
          ) {
            return {
              ...contract,
              'Premium % Gain': newGain,
              'Simulated Premium': newPremium
            };
          }
          return contract;
        });
      }
      return updated;
    });
  };

  // Handle update all contracts with specific settings (from story view)
  const handleUpdateAllContracts = async (days, gain) => {
    // Import recalculateContract from API service
    const { recalculateContract } = await import('../../services/api');

    // Collect all contracts to update
    const contractsToUpdate = [];
    Object.entries(resultsGrouped).forEach(([ticker, contracts]) => {
      contracts.forEach(contract => {
        const expiration = contract.Expiration || contract.expiration;
        contractsToUpdate.push({
          ticker,
          option_type: contract['Option Type'] || contract.option_type || 'call',
          strike: contract.Strike || contract.strike || 0,
          expiration,
          days_to_gain: days,
          stock_gain_pct: gain / 100
        });
      });
    });

    // Update all contracts in parallel
    console.log(`Updating ${contractsToUpdate.length} contracts with days=${days}, gain=${gain}%...`);
    const updatePromises = contractsToUpdate.map(async (contractData) => {
      try {
        const response = await recalculateContract(contractData);
        return {
          ...contractData,
          newGain: response.data.premium_gain_pct,
          newPremium: response.data.simulated_premium,
          success: true
        };
      } catch (err) {
        console.error(`Error updating ${contractData.ticker}:`, err);
        return { ...contractData, success: false };
      }
    });

    const updateResults = await Promise.all(updatePromises);

    // Update state with all new values
    setResultsGrouped(prev => {
      const updated = { ...prev };
      updateResults.forEach(result => {
        if (result.success && updated[result.ticker]) {
          updated[result.ticker] = updated[result.ticker].map(contract => {
            if (
              (contract.Strike || contract.strike) === result.strike &&
              (contract.Expiration || contract.expiration) === result.expiration
            ) {
              return {
                ...contract,
                'Premium % Gain': result.newGain,
                'Simulated Premium': result.newPremium,
                'Days to Gain': days,
                'Underlying Gain %': gain
              };
            }
            return contract;
          });
        }
      });
      return updated;
    });

    const successCount = updateResults.filter(r => r.success).length;
    console.log(`✅ Updated ${successCount}/${contractsToUpdate.length} contracts`);
  };

  // Handle bulk refresh of all visible contracts
  const handleRefreshAll = async () => {
    // Import recalculateContract from API service
    const { recalculateContract } = await import('../../services/api');

    // Get settings for active expiration
    const settings = expirationSettings[activeExpiration] || expirationSettings['all'] || { days: 45, gain: 10 };

    // Collect all contracts to refresh
    const contractsToRefresh = [];
    Object.entries(resultsGrouped).forEach(([ticker, contracts]) => {
      contracts.forEach(contract => {
        // Filter by active expiration if not "all"
        const expiration = contract.Expiration || contract.expiration;
        if (activeExpiration === 'all' || expiration === activeExpiration) {
          contractsToRefresh.push({
            ticker,
            option_type: contract['Option Type'] || contract.option_type || 'call',
            strike: contract.Strike || contract.strike || 0,
            expiration,
            days_to_gain: settings.days,
            stock_gain_pct: settings.gain / 100
          });
        }
      });
    });

    // Refresh all contracts in parallel
    console.log(`Refreshing ${contractsToRefresh.length} contracts...`);
    const refreshPromises = contractsToRefresh.map(async (contractData) => {
      try {
        const response = await recalculateContract(contractData);
        return {
          ...contractData,
          newGain: response.data.premium_gain_pct,
          newPremium: response.data.simulated_premium,
          success: true
        };
      } catch (err) {
        console.error(`Error refreshing ${contractData.ticker}:`, err);
        return { ...contractData, success: false };
      }
    });

    const results = await Promise.all(refreshPromises);

    // Update state with all new values
    setResultsGrouped(prev => {
      const updated = { ...prev };
      results.forEach(result => {
        if (result.success && updated[result.ticker]) {
          updated[result.ticker] = updated[result.ticker].map(contract => {
            if (
              (contract.Strike || contract.strike) === result.strike &&
              (contract.Expiration || contract.expiration) === result.expiration
            ) {
              return {
                ...contract,
                'Premium % Gain': result.newGain,
                'Simulated Premium': result.newPremium
              };
            }
            return contract;
          });
        }
      });
      return updated;
    });

    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Refreshed ${successCount}/${contractsToRefresh.length} contracts`);
  };

  // Check if we have results
  const hasResults = Object.keys(resultsGrouped).length > 0;

  if (!hasResults) {
    return (
      <div className="mobile-results-container">
        <div className="mobile-loading-container">
          <div className="loading-spinner"></div>
          <div className="mobile-loading-text">Loading results...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-results-container">
      {/* Global Simulation Controls */}
      <GlobalSimulationControls
        activeExpiration={activeExpiration}
        expirationSettings={expirationSettings}
        setExpirationSettings={setExpirationSettings}
        resultsGrouped={resultsGrouped}
        onRefreshAll={handleRefreshAll}
        originalSettings={originalSettings}
      />

      {/* View Options Row (Grouped/Individual toggle + Sort) */}
      <ViewOptionsRow
        isGrouped={isGrouped}
        setIsGrouped={setIsGrouped}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {/* Expiration Filter Chips */}
      {allExpirations.length > 0 && (
        <ExpirationFilterChips
          expirations={allExpirations}
          selectedExpirations={selectedExpirations}
          setSelectedExpirations={setSelectedExpirations}
          activeExpiration={activeExpiration}
          setActiveExpiration={setActiveExpiration}
        />
      )}

      {/* Ticker Stories Grid */}
      <TickerStoriesGrid
        resultsGrouped={resultsGrouped}
        selectedExpirations={selectedExpirations}
        onTickerClick={handleTickerClick}
        isGrouped={isGrouped}
        sortBy={sortBy}
      />

      {/* Story View Overlay */}
      {storyViewOpen && currentTicker && (
        <TickerStoryView
          ticker={currentTicker}
          resultsGrouped={resultsGrouped}
          selectedExpirations={selectedExpirations}
          expirationSettings={expirationSettings}
          onClose={handleCloseStoryView}
          onContractGainUpdate={handleContractGainUpdate}
          onUpdateAll={handleUpdateAllContracts}
          originalSettings={originalSettings}
        />
      )}
    </div>
  );
};

export default MobileResultsView;
